<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { MagnifierIcon } from '@solar-icons/vue/linear'
import AppBadge from '../../primitives/AppBadge/AppBadge.vue'
import AppDialog from '../../primitives/AppDialog/AppDialog.vue'
import AppField from '../../primitives/AppField/AppField.vue'
import AppInput from '../../primitives/AppInput/AppInput.vue'
import AppPanelRow from '../../primitives/AppPanel/AppPanelRow.vue'
import AppEmptyState from '../../primitives/AppEmptyState/AppEmptyState.vue'
import { PHASE_LABEL_KEYS } from '../../../lib/strategy/journey-phase'
import { PAGE_TYPE_LABEL_KEYS } from '../../../lib/strategy/page-types'
import type { Page, Topic } from '../../../types/plan'

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    /** The planned pages still to write, the next one in writing order first. */
    pages: readonly Page[]
    /** The topics the pages belong to. */
    topics: readonly Topic[]
    submitting?: boolean
  }>(),
  { submitting: false },
)

const emit = defineEmits<{
  /** The founder picked the page to write. */
  confirm: [pageId: string]
}>()

const { t } = useI18n()

const search = ref('')
const selectedPageId = ref<string | null>(null)

watch(open, (isOpen) => {
  if (!isOpen) return
  search.value = ''
  selectedPageId.value = null
})

const matches = computed(() => {
  const needle = search.value.trim().toLowerCase()
  return needle
    ? props.pages.filter((page) => page.question.toLowerCase().includes(needle))
    : props.pages
})

function selectPage(id: string): void {
  selectedPageId.value = selectedPageId.value === id ? null : id
}

function caption(page: Page): string {
  return [
    props.topics.find((topic) => topic.id === page.topicId)?.name,
    t(PAGE_TYPE_LABEL_KEYS[page.type]),
    t(PHASE_LABEL_KEYS[page.stage]),
  ]
    .filter(Boolean)
    .join(' · ')
}

function confirm(): void {
  if (selectedPageId.value !== null) emit('confirm', selectedPageId.value)
}
</script>

<template>
  <AppDialog
    v-model:open="open"
    dismissible
    width="md"
    :title="t('content.create.page.title')"
    :description="t('content.create.page.description')"
    :confirm-label="t('content.create.page.confirm')"
    :confirm-inactive="selectedPageId === null"
    :confirm-loading="submitting"
    :cancel-label="t('common.cancel')"
    @confirm="confirm"
  >
    <div class="flex min-w-0 flex-col gap-8">
      <AppField v-slot="field" :label="t('content.create.page.label')">
        <AppInput
          v-bind="field"
          v-model="search"
          :placeholder="t('content.create.page.placeholder')"
        >
          <template #leading><MagnifierIcon /></template>
        </AppInput>
      </AppField>

      <div v-if="matches.length > 0" class="-mx-8 flex min-w-0 flex-col gap-1">
        <AppPanelRow
          v-for="(page, index) in matches"
          :key="page.id"
          as="button"
          density="compact"
          :selected="page.id === selectedPageId"
          :aria-pressed="page.id === selectedPageId"
          @click="selectPage(page.id)"
        >
          <span class="flex min-w-0 flex-col gap-1">
            <span class="flex min-w-0 items-baseline gap-6">
              <span class="min-w-0 truncate text-body-sm text-text-primary">{{
                page.question
              }}</span>
              <AppBadge v-if="index === 0 && !search" tone="blue">{{
                t('content.create.page.next')
              }}</AppBadge>
            </span>
            <span class="truncate text-caption text-text-tertiary">{{ caption(page) }}</span>
          </span>
        </AppPanelRow>
      </div>

      <AppEmptyState
        v-else
        size="compact"
        :title="t('content.create.page.emptyTitle')"
        :description="t('content.create.page.emptyBody')"
      />
    </div>
  </AppDialog>
</template>
